/**
 * Navigation + context constants shared by the app shell.
 */
import {
  ListChecks,
  FolderKanban,
  DollarSign,
  Users,
  BarChart3,
  Building2,
  Settings,
  Rocket,
  BrainCircuit,
  BookOpen,
  Database,
  CheckCircle2,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  /** Flat, stroke-based lucide icon (rendered white; inherits `currentColor`). */
  icon: LucideIcon;
  /** Optional sub-pages rendered as an indented, expandable group. */
  children?: NavItem[];
}

/**
 * Left-nav items in display order. The rail is intentionally trimmed: surfaces
 * that belong to a single case's flow (Employers, Retention, State Ops intake)
 * are reached from inside a case, and operational/admin surfaces (Onboarding,
 * Connectors, Integration) are demoted to sub-pages or dropped. Their routes
 * still exist in `App.tsx` for deep links and in-case navigation.
 *
 * Intelligence & Model Governance is a first-class, top-level surface (not an
 * Admin sub-page): it fronts the AI Gateway that powers the agentic/dashboard
 * insights. It is scoped to the Admin/All view only (see `roleViews.ts`);
 * scoped personas omit it to keep each role's rail focused.
 */
export const NAV_ITEMS: NavItem[] = [
  { to: '/queues', label: 'Queues', icon: ListChecks },
  { to: '/cases', label: 'Cases', icon: FolderKanban },
  { to: '/billing', label: 'Client', icon: DollarSign },
  { to: '/team', label: 'Team', icon: Users },
  {
    to: '/reports',
    label: 'Reports',
    icon: BarChart3,
    children: [{ to: '/cfo-portal', label: 'Client Portal', icon: Building2 }],
  },
  {
    to: '/intelligence',
    label: 'Intelligence',
    icon: BrainCircuit,
    children: [
      { to: '/intelligence/knowledge', label: 'Knowledge', icon: BookOpen },
      { to: '/intelligence/memory', label: 'Memory', icon: Database },
      { to: '/intelligence/evals', label: 'Evals', icon: CheckCircle2 },
      { to: '/intelligence/agent-factory', label: 'Agent Factory', icon: Workflow },
    ],
  },
  {
    to: '/admin',
    label: 'Admin',
    icon: Settings,
    children: [{ to: '/onboarding', label: 'Onboarding', icon: Rocket }],
  },
];

/** Flattened list of every nav route (parents + children) for label lookups. */
export function flattenNav(items: NavItem[] = NAV_ITEMS): NavItem[] {
  return items.flatMap((item) => (item.children ? [item, ...item.children] : [item]));
}

export interface Hospital {
  id: string;
  label: string;
}

/** Demo tenants for the hospital switcher (FND-003, multi-tenancy). */
export const HOSPITALS: Hospital[] = [
  { id: 'hosp-001', label: "St. Mary's Medical Center" },
  { id: 'hosp-002', label: 'Riverside General' },
  { id: 'hosp-003', label: 'Mercy Health' },
];

export function hospitalLabel(id: string): string {
  return HOSPITALS.find((h) => h.id === id)?.label ?? id;
}
